// ---------------------------------------------------------------------------
// StatusBar — Bottom bar showing current breakpoint, selection breadcrumb,
// and zoom. When an element is selected, shows the ancestry path as a
// clickable breadcrumb (Figma/Framer-style).
// ---------------------------------------------------------------------------

import { useMemo } from 'react'
import { ChevronRight } from 'lucide-react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { getAncestorPath } from '@/features/page-editor/store/canvas-slice'
import { BREAKPOINTS } from '@/features/page-editor/lib/responsive'

export function StatusBar() {
  const breakpoint = useEditorStore((s) => s.activeBreakpoint)
  const zoom = useEditorStore((s) => s.zoom)
  const selectedIds = useEditorStore((s) => s.selectedIds)
  const selectedBlockName = useEditorStore((s) => s.selectedBlockName)
  const blocks = useEditorStore((s) => s.blocks)
  const app = useEditorStore((s) => s.app)
  const isPreview = useEditorStore((s) => s.isPreview)
  const activePageId = useEditorStore((s) => s.activePageId)
  const pageCount = app?.pages.length ?? 0
  const selectedCount = selectedIds.length

  // Build ancestor path for single selection
  const breadcrumb = useMemo(() => {
    if (selectedCount !== 1 || !selectedBlockName) return null
    const block = blocks[selectedBlockName]
    if (!block) return null
    const path = getAncestorPath(block.ui.root, selectedIds[0])
    if (path.length === 0) return null
    return path.map((el) => ({ id: el.id, type: el.type }))
  }, [selectedIds, selectedBlockName, blocks, selectedCount])

  const handleBreadcrumbClick = (elementId: string) => {
    if (selectedBlockName) {
      useEditorStore.getState().select(elementId, selectedBlockName)
    }
  }

  return (
    <div
      className="flex h-6 items-center justify-between border-t bg-background px-3 text-xs text-muted-foreground"
      data-testid="status-bar"
    >
      <div className="flex items-center gap-3 overflow-hidden">
        {isPreview && (
          <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded font-medium shrink-0">
            Preview
          </span>
        )}
        <span className="shrink-0" data-testid="status-breakpoint">
          Breakpoint: {breakpoint} ({BREAKPOINTS[breakpoint]}px)
        </span>
        {activePageId && <span className="shrink-0">Page: {activePageId}</span>}
        <span className="shrink-0">{pageCount} page{pageCount !== 1 ? 's' : ''}</span>

        {/* Selection breadcrumb or count — hidden in preview mode */}
        {!isPreview && breadcrumb ? (
          <div
            className="flex items-center gap-0.5 overflow-hidden"
            data-testid="selection-breadcrumb"
          >
            <span className="shrink-0 text-muted-foreground/60 mr-0.5">|</span>
            {breadcrumb.map((seg, i) => {
              const isLast = i === breadcrumb.length - 1
              return (
                <span key={seg.id} className="flex items-center gap-0.5 shrink-0">
                  {i > 0 && (
                    <ChevronRight className="h-3 w-3 text-muted-foreground/40 shrink-0" />
                  )}
                  <button
                    type="button"
                    className={`studio-ellipsis max-w-[120px] transition-colors hover:text-foreground ${
                      isLast ? 'text-foreground font-medium' : ''
                    }`}
                    onClick={() => handleBreadcrumbClick(seg.id)}
                    title={`${seg.type} — ${seg.id}`}
                  >
                    {seg.id}
                  </button>
                </span>
              )
            })}
          </div>
        ) : !isPreview && selectedCount > 1 ? (
          <span>{selectedCount} selected</span>
        ) : null}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span>Zoom: {Math.round(zoom * 100)}%</span>
      </div>
    </div>
  )
}
